import React, { useState, useCallback, useMemo, ChangeEvent } from 'react';
import { ExpandMore as DownArrow, KeyboardArrowRight as RightArrow } from '@mui/icons-material';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { SelectedValues } from '@/components/shared/FilterAndSearchPanel/FilterAndSearchPanel';
import { Columns, TemplateTypeWithAllTab } from '@/types';
import { PointOfInterestTable } from '../PointOfInterestTable';
import { abyssColors } from '@/theme/colors';
import { StyledTableCell, TablePaginationContainer } from '../styles';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Accordion,
  IconButton,
  AccordionDetails,
} from '@mui/material';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import { ALL_POIS_TAB_LABEL } from '@/constants';
import { StatusChangeDropDown } from './StatusChangeDropDown';
import { FieldVerificationStatus } from './FieldVerificationStatus';
import { ExpandRowCell, StyledAccordionSummary, StyledTableRow } from './styles';
import { MenuOptions } from './MenuOptions';
import { accordionStyles } from '@/components/UserManagement/styles';
import {
  useGetPointOfInterestGroupedByWorkpackQuery,
  useScCategoryMappingQuery,
} from '@/__generated__/graphql';
import { PageLoader } from '@/components/shared/PageLoader';

type WorkpacksProps = {
  template: TemplateTypeWithAllTab;
  selectedPointOfInterests: string[];
  handlePointOfInterestSelect: (selection: string[]) => void;
  clearSelectedPOIs: () => void;
  selectedTab: string;
  columns: Columns[];
  isVerifiedFilters: boolean[];
  isGoverningFilters: boolean[];
  accessibilityFilters: string[];
  statusFilters: string[];
  selectedPoiFilters: {
    id: string;
    value: string[];
  }[];
  setSelectedFilterValues: React.Dispatch<React.SetStateAction<SelectedValues>>;
  selectedFilterValues: SelectedValues;
  setPaintRegionIds: React.Dispatch<React.SetStateAction<string[]>>;
  paintRegionIds: string[];
  setSelectedPointOfInterests?: React.Dispatch<React.SetStateAction<string[]>>;
  searchTerm?: string;
  handleResetFilters?: () => void;
  setWorkpackIds: React.Dispatch<React.SetStateAction<string[]>>;
  workpackIds: string[];
};

const workpackAccordionColumns = [
  'Workpack',
  'POIs',
  'Workorders',
  'Components',
  'Field Verification',
  'Status',
];

const cellWidth = '14%';
const { primary: colors, warning: warningColor } = abyssColors;

export const Workpacks = ({
  template,
  selectedPointOfInterests,
  handlePointOfInterestSelect,
  clearSelectedPOIs,
  selectedTab,
  columns,
  isVerifiedFilters,
  isGoverningFilters,
  accessibilityFilters,
  statusFilters,
  selectedPoiFilters,
  setSelectedFilterValues,
  selectedFilterValues,
  setPaintRegionIds,
  paintRegionIds,
  searchTerm,
  handleResetFilters,
  setWorkpackIds,
  workpackIds,
}: WorkpacksProps) => {
  const structureId = useGetStructureId();
  const [selectedWorkpack, setSelectedWorkpack] = useState<{ id?: string; rowId?: string }>({});
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 30 });

  const { data: scCategoryData, loading: scCategoryLoading } = useScCategoryMappingQuery({
    variables: { structureId },
    skip: !structureId,
  });

  const type =
    selectedTab === ALL_POIS_TAB_LABEL ? undefined : selectedTab.replace(/ /g, '_').toUpperCase();

  const { data: workpacksData, loading } = useGetPointOfInterestGroupedByWorkpackQuery({
    variables: {
      input: {
        structureId,
        type,
        searchTerm,
      },
      pageInfo: {
        skip: pagination.page * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortBy: 'createdAt',
        sortDir: 1,
      },
    },
  });

  const { workpacks, totalCount } = useMemo(() => {
    const { allWorkpacks, workpacksCount } = workpacksData ?? {};

    const sortedByUnassigned = allWorkpacks
      ? allWorkpacks.slice().sort((a, b) => {
          if (a._id === null) return 1;
          if (b._id === null) return -1;
          return 0;
        })
      : [];

    return {
      workpacks: sortedByUnassigned,
      totalCount: workpacksCount?.count ?? 100,
    };
  }, [workpacksData]);

  const handleExpandClick = useCallback(
    (id: string) => {
      setSelectedWorkpack((previous) => {
        const previousRowId = previous.rowId;
        const newId = id === previousRowId ? undefined : id;
        const isUnassignedWorkpack = id !== previousRowId && id === '';
        return { id: isUnassignedWorkpack ? 'unassigned' : newId, rowId: newId };
      });
      handleResetFilters?.();
    },
    [handleResetFilters]
  );

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = +event.target.value;
    setPagination({ page: 0, rowsPerPage: newRowsPerPage });
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPagination((previous) => ({ ...previous, page: newPage }));
  };

  const getColor = useCallback(
    (id: string) => {
      if (!id) return warningColor[50];
      return selectedWorkpack.id === id ? colors[100] : colors[50];
    },
    [selectedWorkpack.id]
  );

  const scCategoryMappingExists = useMemo(() => {
    return Object.keys(scCategoryData?.structure?.scCategoryMapping ?? {}).length > 0;
  }, [scCategoryData?.structure?.scCategoryMapping]);

  if (loading) {
    return (
      <Box sx={{ mt: 25 }}>
        <PageLoader />
      </Box>
    );
  }

  if (!workpacks || workpacks?.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: 10 }}>
        <FeedbackCircle text="No data found" />
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '2%' }}></TableCell>
            {workpackAccordionColumns.map((column) => (
              <TableCell key={column} sx={{ width: cellWidth }}>
                {column}
              </TableCell>
            ))}
            <TableCell sx={{ width: cellWidth }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {workpacks.map((data) => {
            const workpackId = data?._id ?? '';
            const isOpen = selectedWorkpack.rowId === workpackId;
            const border = isOpen ? `1px solid ${colors[300]}` : 'none';
            const workpackIdForPoiTable =
              selectedWorkpack.id === 'unassigned' ? undefined : selectedWorkpack.id ?? '';
            return (
              <StyledTableRow key={workpackId}>
                <TableCell colSpan={8} style={{ padding: 0, borderBottom: 'none' }}>
                  <Accordion
                    disableGutters
                    elevation={0}
                    expanded={isOpen}
                    sx={{ ...accordionStyles.root, mb: 1 }}
                  >
                    <StyledAccordionSummary sx={{ border, backgroundColor: getColor(workpackId) }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <ExpandRowCell>
                          <IconButton onClick={() => handleExpandClick(workpackId)}>
                            {isOpen ? <DownArrow /> : <RightArrow />}
                          </IconButton>
                        </ExpandRowCell>
                        <StyledTableCell sx={{ fontWeight: 500 }}>
                          {workpackId ? data.workpack?.name : 'Un-assigned'}
                        </StyledTableCell>
                        <StyledTableCell>{data.pointOfInterestsCount}</StyledTableCell>
                        <StyledTableCell>N/A</StyledTableCell>
                        <StyledTableCell>{data.assembliesCount}</StyledTableCell>
                        <StyledTableCell>
                          <FieldVerificationStatus
                            pointOfInterestsCount={data.pointOfInterestsCount}
                            verifiedPointOfInterestsCount={data.verifiedPointOfInterestsCount}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          {workpackId && !scCategoryLoading && (
                            <StatusChangeDropDown
                              workpackId={workpackId}
                              status={data.workpack?.status}
                              showAlternateStatuses={scCategoryMappingExists}
                            />
                          )}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {workpackId && data.workpack?.name && (
                            <MenuOptions
                              workpackId={workpackId}
                              workpackName={data.workpack.name}
                            />
                          )}
                        </StyledTableCell>
                      </Box>
                    </StyledAccordionSummary>
                    <AccordionDetails>
                      {isOpen && (
                        <PointOfInterestTable
                          template={template}
                          selectedPointOfInterests={selectedPointOfInterests}
                          handlePointOfInterestSelect={handlePointOfInterestSelect}
                          clearSelectedPOIs={clearSelectedPOIs}
                          selectedTab={selectedTab}
                          setSelectedFilterValues={setSelectedFilterValues}
                          selectedFilterValues={selectedFilterValues}
                          setPaintRegionIds={setPaintRegionIds}
                          paintRegionIds={paintRegionIds}
                          isVerifiedFilters={isVerifiedFilters}
                          accessibilityFilters={accessibilityFilters}
                          statusFilters={statusFilters}
                          selectedPoiFilters={selectedPoiFilters}
                          columns={columns}
                          workpackId={workpackIdForPoiTable}
                          excludePoisWithWorkpack={workpackIdForPoiTable === undefined}
                          viewBy={'workpack'}
                          setWorkpackIds={setWorkpackIds}
                          workpackIds={workpackIds}
                          isGoverningFilters={isGoverningFilters}
                        />
                      )}
                    </AccordionDetails>
                  </Accordion>
                </TableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePaginationContainer>
        <TablePagination
          count={totalCount}
          page={pagination.page}
          rowsPerPageOptions={[
            { label: '30', value: 30 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
            { label: 'All', value: totalCount },
          ]}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onPageChange={handleChangePage}
        />
      </TablePaginationContainer>
    </TableContainer>
  );
};
